import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useLeadPhoneCallOutcomesQuery } from '../../../../queries/Lead/useLeadPhoneCallOutcomesQuery';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { TabToggle } from '../../../../web/components-v2/TabToggle';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../../web/components/UserCombobox';
import { FieldKey } from '../../../viewModels/form';
import { toolbarStyleSheet } from './toolbarStyles';

export type FollowUpsViewType = 'card' | 'row';

const VIEW_ITEMS = [
	{ value: 'row', label: 'List' },
	{ value: 'card', label: 'Cards' },
];

interface IProps {
	sdr: Api.IUser | null;
	onSdrChange: (user: Api.IUser | null) => void;
	outcome: FieldKey;
	onOutcomeChange: (outcome: FieldKey) => void;
	viewType: FollowUpsViewType;
	onViewTypeChange: (viewType: FollowUpsViewType) => void;
}

export const FollowUpsToolbar: React.FC<IProps> = ({
	sdr,
	onSdrChange,
	outcome,
	onOutcomeChange,
	viewType,
	onViewTypeChange,
}) => {
	const inputId = React.useId();
	const outcomesQuery = useLeadPhoneCallOutcomesQuery({
		meta: { toastErrorMessage: 'Unable to load outcomes' },
	});
	const outcomeItems = [
		{ value: FieldKey.Unknown, label: 'All outcome types' },
		...(outcomesQuery.data ?? []).map(option => ({ value: option.name, label: option.label })),
	];

	return (
		<div className={css(toolbarStyleSheet.controls)}>
			<div className={css(toolbarStyleSheet.sdr)}>
				{/* Keyed by selection so picking or clearing an SDR resets the search text. */}
				<UserCombobox
					id={sdr?.id}
					key={sdr?.id ?? 'all'}
					onValueChange={(user: Api.IUser | null) => onSdrChange(user ?? null)}
				>
					<ComboboxInput
						id={`${inputId}-sdr`}
						aria-label='Filter by SDR'
						styles={[toolbarStyleSheet.controlHeight]}
						placeholder='Filter by SDR'
						showClear
					/>
					<ComboboxContent>
						<UserComboboxStatus />
						<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
			</div>
			<div className={css(toolbarStyleSheet.outcome)}>
				<Select
					items={outcomeItems}
					value={outcome}
					onValueChange={(value: FieldKey) => onOutcomeChange(value ?? FieldKey.Unknown)}
				>
					<SelectTrigger aria-label='Outcome type' styles={toolbarStyleSheet.selectTrigger}>
						<SelectValue placeholder='All outcome types' />
					</SelectTrigger>
					<SelectContent>
						{outcomeItems.map(item => (
							<SelectItem key={item.value} value={item.value}>
								{item.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<TabToggle
				items={VIEW_ITEMS}
				value={viewType}
				aria-label='Follow ups view'
				onValueChange={value => onViewTypeChange(value as FollowUpsViewType)}
			/>
		</div>
	);
};
